import {
  convertKeysToCamelCase,
  convertKeysToSnakeCase,
} from "@/lib/change-case";
import type { Socket } from "socket.io-client";

/**
 * Adds camelCase↔︎snake_case transformations to the given Socket instance.
 * Incoming payloads are converted to camelCase, outgoing to snake_case.
 */
export function setupSocketCaseConverter(socket: Socket): void {
  const originalOn = socket.on.bind(socket);
  const originalOff = socket.off.bind(socket);
  const originalEmit = socket.emit.bind(socket);

  // Map to track wrapper functions so off() can remove the correct listener
  // WeakMap ensures no memory leaks if listeners are garbage collected
  // biome-ignore lint/suspicious/noExplicitAny: need to store function references
  const listenerMap = new WeakMap<any, any>();

  socket.on = function (
    this: Socket,
    event: string,
    // biome-ignore lint/suspicious/noExplicitAny: socket.io listener args are dynamic
    listener: (...args: any[]) => void,
  ) {
    // Check if we already have a wrapper for this listener
    let wrapper = listenerMap.get(listener);

    if (!wrapper) {
      // Create a new wrapper that transforms incoming payloads
      // biome-ignore lint/suspicious/noExplicitAny: socket.io payloads are dynamic
      // biome-ignore lint/suspicious/noExplicitAny: wrapper function needs any type for socket.io compatibility
      wrapper = ((...args: any[]) => {
        const transformedArgs = args.map((arg) => convertKeysToCamelCase(arg));
        listener(...transformedArgs);
        // biome-ignore lint/suspicious/noExplicitAny: wrapper function return type must match socket.io listener type
      }) as (...args: any[]) => void;
      // Store the mapping so we can find it later for off()
      listenerMap.set(listener, wrapper);
    }

    // biome-ignore lint/suspicious/noExplicitAny: wrapper is a function
    // biome-ignore lint/suspicious/noExplicitAny: socket.on return type requires any
    return originalOn(event, wrapper as any);
    // biome-ignore lint/suspicious/noExplicitAny: socket.on return type requires any for proper socket.io typing
  } as any;

  socket.off = function (
    this: Socket,
    // biome-ignore lint/suspicious/noExplicitAny: event type must match socket.io's flexible event system
    event: any,
    // biome-ignore lint/suspicious/noExplicitAny: socket.io listener args are dynamic
    listener?: (...args: any[]) => void,
  ) {
    if (listener) {
      // Look up the wrapper function for this listener
      const wrapper = listenerMap.get(listener);
      if (wrapper) {
        // Remove the wrapper, not the original listener
        // biome-ignore lint/suspicious/noExplicitAny: wrapper is a function
        return originalOff(event, wrapper as any);
      }
    }
    // If no listener provided or no wrapper found, call original off
    // biome-ignore lint/suspicious/noExplicitAny: listener type requires any
    return originalOff(event, listener as any);
    // biome-ignore lint/suspicious/noExplicitAny: socket.off return type requires any for proper socket.io typing
  } as any;

  // biome-ignore lint/suspicious/noExplicitAny: socket.io payloads are dynamic
  // biome-ignore lint/suspicious/noExplicitAny: socket.emit return type requires any for proper socket.io typing
  socket.emit = function (this: Socket, event: string, ...args: any[]) {
    const transformedArgs = args.map((arg) => convertKeysToSnakeCase(arg));
    return originalEmit(event, ...transformedArgs);
    // biome-ignore lint/suspicious/noExplicitAny: socket.emit return type must match socket.io's return type
  } as any;
}
